import useAuthStore from '../store/auth/auth.store';
import type { IUserInfo } from '../types/components';
import type { IApiResponse } from './api';

/**
 * Permission refresh utility.
 * Detects permission version changes from API responses and triggers
 * an automatic refresh of user permissions.
 */

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

/**
 * Check if the permission version has changed and trigger a refresh if needed.
 * This is called from the HTTP client after every authenticated request.
 */
export function checkPermissionVersion(newVersion: string): void {
  const currentVersion = useAuthStore.getState().getPermissionVersion();

  // Skip if versions match or no current version (first load)
  if (!currentVersion || currentVersion === newVersion) {
    return;
  }

  // Trigger permission refresh
  triggerPermissionRefresh();
}

/**
 * Trigger a permission refresh by calling /me endpoint.
 * This is debounced to prevent multiple simultaneous refreshes.
 */
export function triggerPermissionRefresh(): void {
  // Don't trigger if already refreshing
  if (isRefreshing) {
    return;
  }

  isRefreshing = true;

  // Use dynamic import to avoid circular dependency with bearerClient
  refreshPromise = refreshPermissions().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });
}

/**
 * Get the current refresh promise if one is in progress.
 * Useful for waiting on permission refresh to complete.
 */
export function getRefreshPromise(): Promise<void> | null {
  return refreshPromise;
}

/**
 * Refresh user permissions by calling /me endpoint.
 */
async function refreshPermissions(): Promise<void> {
  try {
    // Dynamic import to avoid circular dependency
    const { bearerClient } = await import('./http');

    const response = await bearerClient.get<IApiResponse<IUserInfo>>({
      apiPath: '/v1/me',
    });

    if (response?.data) {
      const { setUserPermissions, setPermissionVersion } =
        useAuthStore.getState();

      // Update permissions
      if (response.data.permissions) {
        setUserPermissions(response.data.permissions);
      }

      // Update version
      if (response.data.permission_version) {
        setPermissionVersion(response.data.permission_version);
      }
    }
  } catch (error) {
    // Log but don't fail - permission refresh is best-effort
    console.error('Failed to refresh permissions:', error);
  }
}
